'use client';
import { useTranslations } from 'next-intl';
import { type ReactNode, useMemo } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { EditPicture } from '@/app/components/icons/EditPicture';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { ProductLineVideoReference } from '@/app/components/icons/ProductLineVideoReference';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaPromptPreview } from '@/app/components/shared/MediaPromptPreview';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { SITE } from '@/app/constants';
import type { ImagePrompt } from '@/app/types';
import { cn, convertComponents2Assets } from '@/app/utils';
import type { ViduV1Element } from './types';

type ObjectInfo = {
  className?: string;
  textElementConfig?: {
    className?: string;
    textClassName?: string;
  };
  hoverOperations?: ReactNode[];
  slotAfterName?: ReactNode;
  deleteSlot?: ReactNode;
  checkSlot?: ReactNode;
  forbidden?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  editSlot?: ReactNode;
  tag?: string;
  showImgIcon?: boolean;
  footerClassName?: string;
  nameClassName?: string;
  forbiddenContent?: string;
  imgsContentClassName?: string;
  previewClassName?: string;
};

type ImageObject = {
  type: 'image';
  imagePrompt: ImagePrompt;
  name: string;
} & ObjectInfo;

type ElementObject = {
  type: 'element';
  element: ViduV1Element;
} & ObjectInfo;

export type ElementCardProps = ImageObject | ElementObject;

const BgColorMap: Record<string, string> = {
  element: 'bg-system-black36',
  image: 'bg-system-black36',
  share_reference: 'bg-system-black36',
};

export const ElementCard = (props: ElementCardProps) => {
  const {
    className,
    textElementConfig,
    hoverOperations,
    slotAfterName,
    checkSlot,
    deleteSlot,
    type,
    forbidden,
    onClick,
    onEdit,
    editSlot,
    tag,
    showImgIcon = false,
    footerClassName,
    nameClassName,
    forbiddenContent,
    imgsContentClassName,
    previewClassName,
  } = props;

  const finalElement = useMemo(() => {
    if (props.type === 'image') {
      return {
        ...props,
        element: {
          type: 'user',
          name: props.name,
          imagePrompts: [props.imagePrompt],
          isShare: false,
          modality: 'image' as const,
        },
      };
    }
    return {
      ...props,
      element: {
        ...props.element,
        imagePrompts: forbidden ? [] : convertComponents2Assets(props.element.components),
        isShare: props.element.share_meta?.status === 'approved',
      },
    };
  }, [props, forbidden]);

  const isTextModality = finalElement.element.modality === 'text';
  const showWatermark = SITE === 'china' && props.type === 'element' && props.element.source === 'element_task';

  const t = useTranslations('elements');
  return (
    <div
      className={cn(
        'relative box-border flex max-h-[137px] max-w-[106px] flex-col rounded-12 border border-transparent px-[3px] pt-[3px] pb-1.5',
        BgColorMap[type === 'element' && finalElement.element.isShare ? 'share_reference' : type],
        forbidden && 'bg-[#323845] bg-none',
        className,
      )}
    >
      <div className={cn('group relative aspect-square max-w-[98px] flex-1', previewClassName)}>
        <div className={cn('aspect-square w-full overflow-hidden', imgsContentClassName)}>
          {forbidden ? (
            <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-8 bg-system-black36">
              {forbiddenContent ? (
                <div className="p-1 text-center text-[9px] text-system-white36 leading-[1.2]">{forbiddenContent}</div>
              ) : (
                <BaseLineExclamation className="text-2xl text-system-white36" />
              )}
            </div>
          ) : isTextModality ? (
            <TextElementCover
              name={finalElement.element?.name}
              className={cn('rounded-8 text-xs', textElementConfig?.className)}
              textClassName={textElementConfig?.textClassName}
              onClick={onClick}
            />
          ) : (
            <>
              <MediaPromptPreview
                prompt={finalElement.element.imagePrompts?.[0] || null}
                className="rounded-8"
                onClick={onClick}
              />
              {showWatermark && <MediaWithWatermark overlay width={1} height={1} showWatermark />}
            </>
          )}
        </div>

        {/* Hover controls - checkbox, view, edit */}
        {(hoverOperations?.length || 0) > 0 && (
          <div className="absolute inset-0 flex items-center justify-center rounded-8 bg-black/40 text-xl opacity-0 transition-opacity group-hover:opacity-100 sm:hidden">
            <div className="flex gap-3">{hoverOperations?.map((it) => it)}</div>
          </div>
        )}
        {checkSlot}
        {deleteSlot && (
          <div className="absolute top-[-10px] right-[-6px] z-10 hidden items-center justify-center transition-opacity group-hover:block sm:group-hover:hidden">
            {deleteSlot}
          </div>
        )}
        {(onEdit || editSlot) && (
          <>
            {editSlot}
            {onEdit && (
              <div
                className="absolute top-1 right-1 z-50 hidden h-6 w-6 cursor-pointer items-center justify-center rounded-4 bg-[rgba(0,0,0,.5)] p-[1px] text-system-white text-xl sm:flex"
                onClick={onEdit}
              >
                <EditPicture className="size-4" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Demo label */}
      {finalElement.element?.type === 'preset' && (
        <div className="absolute top-2 right-2 rounded-full bg-system-blue02 px-2 py-0.5 text-[10px] text-white sm:top-1.5 sm:right-auto sm:left-1.5 sm:px-1.5">
          {t('demo')}
        </div>
      )}
      {finalElement.element?.type === 'user' && tag && (
        <div className="absolute top-2 right-2 max-w-16 rounded-full bg-system-blue02 px-2 py-0.5 text-[10px] text-white sm:top-1.5 sm:right-auto sm:left-1.5 sm:px-1.5">
          <CustomTooltip showOnlyOnOverflow={true} maxLines={1} content={tag}>
            {tag}
          </CustomTooltip>
        </div>
      )}
      <div className={cn('flex items-center pt-2 pb-[7px]', footerClassName)}>
        <div className={cn('flex flex-1 items-center overflow-hidden', nameClassName)}>
          {type === 'element' &&
            (finalElement?.element?.modality === 'video' ? (
              <ProductLineVideoReference
                className={cn(
                  'flex-shrink-0 text-base text-system-blue02',
                  forbidden && 'text-system-white/40',
                  !forbidden && finalElement.element.isShare && 'text-[#AB8DFF]',
                )}
              />
            ) : (
              <ProductLineElement
                className={cn(
                  'flex-shrink-0 text-base text-system-blue02',
                  forbidden && 'text-system-white/40',
                  !forbidden && finalElement.element.isShare && 'text-[#AB8DFF]',
                )}
              />
            ))}
          {type === 'image' && showImgIcon && (
            <ProductLinePic className={cn('flex-shrink-0 text-base text-system-white/40')} />
          )}
          {/* Category label */}
          <CustomTooltip
            sideOffset={2}
            content={finalElement.element?.name}
            maxLines={1}
            showOnlyOnOverflow={true}
            trigger="auto"
          >
            <div className={cn('ml-1 text-xs', forbidden && 'text-system-white64')}>{finalElement.element?.name}</div>
          </CustomTooltip>
        </div>
        <div className="mr-2 flex-shrink-0">{slotAfterName}</div>
      </div>
    </div>
  );
};
